/* eslint-disable sort-imports/sort-imports */
import '@formatjs/intl-getcanonicallocales/polyfill';
import '@formatjs/intl-locale/polyfill';
import '@formatjs/intl-pluralrules/polyfill';
import '@formatjs/intl-pluralrules/locale-data/en';
import '@formatjs/intl-pluralrules/locale-data/es';
import '@formatjs/intl-pluralrules/locale-data/fr';
import '@formatjs/intl-pluralrules/locale-data/zh';
import '@formatjs/intl-pluralrules/locale-data/vi';
import '@formatjs/intl-numberformat/polyfill';
import '@formatjs/intl-numberformat/locale-data/en';
import '@formatjs/intl-numberformat/locale-data/en-AU';
import '@formatjs/intl-numberformat/locale-data/en-CA';
import '@formatjs/intl-numberformat/locale-data/es';
import '@formatjs/intl-numberformat/locale-data/fr';
import '@formatjs/intl-numberformat/locale-data/zh';
import moment from 'moment';
import 'moment/locale/en-au';
import 'moment/locale/en-ca';
import 'moment/locale/es';
import 'moment/locale/fr';
import 'moment/locale/vi';
import 'moment/locale/zh-cn';

import * as Localization from 'expo-localization';

// eslint-disable-next-line no-restricted-imports
import i18n from 'i18n-js';
import { ConsumptionTaxName, PackUnit } from '@/enums';
import { Platform } from 'react-native';
import { CompleteTranslation } from '@/types';

export const createTranslation = (en: string): CompleteTranslation => ({
  en,
  es: '',
  fr: '',
  zh: '',
});

export enum TranslatedLanguage {
  Chinese = 'zh',
  English = 'en',
  EnglishAU = 'en-AU',
  EnglishCA = 'en-CA',
  French = 'fr',
  Spanish = 'es',
  Vietnamese = 'vi',
}

export const DEFAULT_LOCALE = TranslatedLanguage.English;

moment.updateLocale('en', {
  longDateFormat: {
    // @ts-ignore property exists
    ...moment.localeData('en')._longDateFormat,
    LT: 'h:mma',
  },
  weekdaysMin: ['Su', 'M', 'T', 'W', 'Th', 'F', 'Sa'],
});

const defaultLanguageCode =
  Localization.getLocales()[0]?.languageCode ?? DEFAULT_LOCALE;
const supportedMomentLocales: Record<string, string> = {
  es: 'es',
  fr: 'fr',
  vi: 'vi',
  zh: 'zh-cn',
};

if (supportedMomentLocales[defaultLanguageCode]) {
  moment.locale(supportedMomentLocales[defaultLanguageCode]);
}

// Polyfills Intl.DateTimeFormat and Date.prototype.toLocaleDateString on iOS
// since Hermes doesn't support some IANA timezones
// https://github.com/facebook/hermes/issues/1100
(() => {
  if (Platform.OS !== 'ios') {
    return;
  }

  const TIMEZONE_ALIAS_MAP: Record<string, string> = {
    'Asia/Kolkata': 'Asia/Calcutta',
  };

  const OriginalDateTimeFormat = Intl.DateTimeFormat;

  Intl.DateTimeFormat = function (
    this: Intl.DateTimeFormat,
    locales?: string | string[],
    options?: Intl.DateTimeFormatOptions,
  ) {
    if (options?.timeZone && TIMEZONE_ALIAS_MAP[options.timeZone]) {
      options = {
        ...options,
        timeZone: TIMEZONE_ALIAS_MAP[options.timeZone],
      };
    }

    return new OriginalDateTimeFormat(locales, options);
  } as Intl.DateTimeFormatConstructor;

  // Copy static methods and properties to the updated constructor
  for (const name of Object.getOwnPropertyNames(OriginalDateTimeFormat)) {
    if (name !== 'length' && name !== 'name' && name !== 'prototype') {
      Object.defineProperty(
        Intl.DateTimeFormat,
        name,
        Object.getOwnPropertyDescriptor(OriginalDateTimeFormat, name)!,
      );
    }
  }

  // Link prototypes to so instanceof checks work
  Object.defineProperty(Intl.DateTimeFormat, 'prototype', {
    configurable: false,
    enumerable: false,
    value: OriginalDateTimeFormat.prototype,
    writable: false,
  });

  // Also polyfill Date.prototype.toLocaleDateString
  const OriginalToLocaleDateString = Date.prototype.toLocaleDateString;

  Date.prototype.toLocaleDateString = function (
    this: Date,
    locales?: string | string[],
    options?: Intl.DateTimeFormatOptions,
  ) {
    if (options?.timeZone && TIMEZONE_ALIAS_MAP[options.timeZone]) {
      options = {
        ...options,
        timeZone: TIMEZONE_ALIAS_MAP[options.timeZone],
      };
    }

    return OriginalToLocaleDateString.call(this, locales, options);
  };
})();

const translationsMap: { [language in TranslatedLanguage]: NodeModule } = {
  [TranslatedLanguage.Chinese]: require('@assets/translations/zh.json'),
  [TranslatedLanguage.English]: require('@assets/translations/en.json'),
  [TranslatedLanguage.EnglishAU]: require('@assets/translations/en-AU.json'),
  [TranslatedLanguage.EnglishCA]: require('@assets/translations/en-CA.json'),
  [TranslatedLanguage.French]: require('@assets/translations/fr.json'),
  [TranslatedLanguage.Spanish]: require('@assets/translations/es.json'),
  [TranslatedLanguage.Vietnamese]: require('@assets/translations/vi.json'),
};

// Change the default separator so '.' can be used in translation keys
i18n.defaultSeparator = '|';
i18n.fallbacks = true;
i18n.locale = defaultLanguageCode;
i18n.missingTranslation = (x) => x;

// A bad key renders on every row of every list that uses it, so report each
// distinct problem once per session rather than flooding Sentry.
const reportedPlaceholders = new Set<string>();

const reportOnce = (error: Error, signature: string) => {
  if (reportedPlaceholders.has(signature)) {
    return;
  }

  reportedPlaceholders.add(signature);

  // Required lazily rather than imported at the top. `@/utils/sentry` reaches
  // `@/contexts/dynamic-config`, which imports back into this module, so a
  // static import closes a cycle and leaves LOCALIZED_PACK_UNITS undefined for
  // whichever side loads first. Nothing calls this during module init.
  const { captureException } =
    require('@/utils/sentry') as typeof import('@/utils/sentry');

  captureException(error);
};

// i18n-js renders `[missing {{count}} value]` straight into the UI when a
// translation references a placeholder the call site did not supply — which is
// exactly what a locale file that lost a placeholder in translation produces.
// Nothing fails, so it ships and sits there. Fail loud instead: throw where a
// developer or CI will see it, report where they will not.
i18n.missingPlaceholder = (placeholder, message) => {
  const error = new Error(
    `i18n: no value supplied for ${placeholder} in "${message}"`,
  );

  if (__DEV__ || process.env.NODE_ENV === 'test') {
    throw error;
  }

  reportOnce(error, `missing|${placeholder}|${message}`);

  // Better than `[missing {{count}} value]`: a gap in a sentence reads as a
  // typo, the default reads as a crash.
  return '';
};

// `nullPlaceholder` delegates to `missingPlaceholder` unless it is overridden,
// so it needs its own handler. An explicitly-passed null or undefined is a
// call-site data question rather than locale drift, and optional values are
// common enough that crashing a dev screen over one would be noise. Report it
// and move on.
i18n.nullPlaceholder = (placeholder, message) => {
  reportOnce(
    new Error(`i18n: null value supplied for ${placeholder} in "${message}"`),
    `null|${placeholder}|${message}`,
  );

  return '';
};

i18n.translations = translationsMap;

export const LOCALIZED_PACK_UNITS = Object.values(PackUnit).reduce(
  (acc, unit) => {
    acc[unit] = i18n.t(`pack_unit_${unit.toLowerCase()}`);
    return acc;
  },
  {} as Record<PackUnit, string>,
);

export const ABBREVIATED_PACK_UNITS = Object.values(PackUnit).reduce(
  (acc, unit) => {
    const key = `pack_unit_abbr_${unit.toLowerCase()}`;
    const translated = i18n.t(key);
    acc[unit] = translated === key ? LOCALIZED_PACK_UNITS[unit] : translated;
    return acc;
  },
  {} as Record<PackUnit, string>,
);

// Consumption tax names (e.g. "Sales Tax") are configured per distributor on the
// backend and arrive as raw strings, so they must be localized at render time.
// Looks up a `consumption_tax_*` key and falls back to the raw name when a
// translation is not defined (keeps GST/HST rendering as-is).
export const getLocalizedConsumptionTaxName = (
  name: ConsumptionTaxName,
): string => {
  const key = `consumption_tax_${name.toLowerCase().replace(/\s+/g, '_')}`;
  const translated = i18n.t(key);
  return translated === key ? name : translated;
};

// Map language identifiers to translated languages
const LANGUAGE_TO_MACRO_LANGUAGE_MAP: Record<string, TranslatedLanguage> = {
  wuu: TranslatedLanguage.Chinese,
  yue: TranslatedLanguage.Chinese,
};

// Convert language map to an array of [search regex, replacement string]
const LANGUAGE_TO_MACRO_LANGUAGES: [RegExp, string][] = Object.entries(
  LANGUAGE_TO_MACRO_LANGUAGE_MAP,
).map(([language, macroLanguage]) => [
  new RegExp(`^${language}-`, 'i'),
  `${macroLanguage}-`,
]);

const SUPPORTED_LANGUAGES = new Set<string>(Object.values(TranslatedLanguage));

// ** Used for the HTML lang attribute on web **
// supported language that will be used by i18n-js for translations
// checks the full locale first (e.g. 'en-AU'), then the base language code (e.g. 'en'), with en fallback
export const getEffectiveLanguage = (): string => {
  const locale = i18n.locale;
  if (SUPPORTED_LANGUAGES.has(locale)) {
    return locale;
  }
  const baseLanguage = locale.split('-')[0];
  if (SUPPORTED_LANGUAGES.has(baseLanguage)) {
    return baseLanguage;
  }
  return DEFAULT_LOCALE;
};

export const getLocale = () => {
  const currentLocales = Localization.getLocales();
  let locale = currentLocales[0]?.languageTag ?? defaultLanguageCode;

  for (const [language, macroLanguage] of LANGUAGE_TO_MACRO_LANGUAGES) {
    locale = locale.replace(language, macroLanguage);
  }

  i18n.locale = locale;
  return locale;
};

export default i18n;
